import {
  mergeRuntimeGraphPartitions,
  RuntimeDiscoveryDatasetSchema,
  RuntimeGraphContextDatasetSchema,
  RuntimeGraphCoreDatasetSchema,
  RuntimeGraphDatasetSchema,
  RuntimeGraphEvidenceDatasetSchema,
  RuntimeGraphManifestSchema,
  type RuntimeDiscoveryDataset,
  type RuntimeGraphContextDataset,
  type RuntimeGraphCoreDataset,
  type RuntimeGraphDataset,
  type RuntimeGraphEvidenceDataset,
  type RuntimeGraphManifest,
} from '@entangled-vega/shared';

import type { GraphDataset, GraphRelationship, RelationshipKind } from './graph-types.js';

const MANIFEST_URL = '/data/manifest.json';

export type RuntimeLoadPhase =
  | 'manifest-ready'
  | 'discovery-bytes'
  | 'discovery-verified'
  | 'discovery-ready'
  | 'graph-bytes'
  | 'graph-verified'
  | 'context-bytes'
  | 'context-verified'
  | 'evidence-bytes'
  | 'evidence-verified'
  | 'graph-ready';

type LoadOptions = {
  fetcher?: typeof fetch;
  signal?: AbortSignal;
  manifest?: RuntimeGraphManifest;
  onPhase?: (phase: RuntimeLoadPhase) => void;
};

function browserFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return globalThis.fetch(input, init);
}

export class GraphLoadError extends Error {
  override name = 'GraphLoadError';
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function fetchText(options: {
  fetcher: typeof fetch;
  url: string;
  signal?: AbortSignal;
  cache: RequestCache;
}): Promise<string> {
  const response = await options.fetcher(options.url, {
    cache: options.cache,
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  if (!response.ok) {
    throw new GraphLoadError(`Graph request failed with HTTP ${response.status}.`);
  }
  return response.text();
}

function assetUrl(path: string, kind: 'discovery' | 'graph' | 'context' | 'evidence'): string {
  if (!new RegExp(`^assets/${kind}\\.[a-f0-9]{16}\\.json$`, 'u').test(path)) {
    throw new GraphLoadError(`The graph manifest contains an invalid ${kind} asset path.`);
  }
  return `/data/${path}`;
}

function assertPartitionMetadata(
  manifest: RuntimeGraphManifest,
  partition: RuntimeGraphCoreDataset | RuntimeGraphContextDataset | RuntimeGraphEvidenceDataset,
): void {
  if (
    partition.datasetVersion !== manifest.datasetVersion ||
    partition.configVersion !== manifest.configVersion ||
    partition.policyVersion !== manifest.policyVersion ||
    partition.layoutVersion !== manifest.layoutVersion ||
    partition.generatedAt !== manifest.generatedAt ||
    partition.snapshotDate !== manifest.snapshotDate
  ) {
    throw new GraphLoadError('The graph manifest and partition are from incompatible versions.');
  }
}

function reportPhase(options: LoadOptions, phase: RuntimeLoadPhase): void {
  performance.mark(`entangled-vega:${phase}`);
  options.onPhase?.(phase);
}

function relationshipKind(
  relationship: RuntimeGraphDataset['relationships'][number],
): RelationshipKind {
  if (relationship.lens === 'family') {
    return 'family';
  }
  return relationship.relation === 'spouse' ? 'spouse' : 'unmarried-partner';
}

function toGraphDataset(runtime: RuntimeGraphDataset): GraphDataset {
  const relationships: GraphRelationship[] = runtime.relationships.map((relationship) => ({
    ...relationship,
    kind: relationshipKind(relationship),
  }));
  return {
    version: runtime.datasetVersion,
    generatedAt: runtime.generatedAt,
    snapshotDate: runtime.snapshotDate,
    fixture: false,
    people: runtime.people,
    relationships,
    sources: runtime.sources,
    indexes: runtime.indexes,
    featuredTraces: runtime.featuredTraces,
    limitations: runtime.limitations,
  };
}

function assertCompatible(manifest: RuntimeGraphManifest, runtime: RuntimeGraphDataset): void {
  if (
    runtime.datasetVersion !== manifest.datasetVersion ||
    runtime.configVersion !== manifest.configVersion ||
    runtime.policyVersion !== manifest.policyVersion ||
    runtime.layoutVersion !== manifest.layoutVersion
  ) {
    throw new GraphLoadError('The graph manifest and data asset are from incompatible versions.');
  }
  if (
    runtime.people.length !== manifest.counts.people ||
    runtime.relationships.length !== manifest.counts.relationships ||
    runtime.sources.length !== manifest.counts.sources ||
    runtime.featuredTraces.length !== manifest.counts.featuredTraces
  ) {
    throw new GraphLoadError('The graph manifest counts do not match the data asset.');
  }
  if (runtime.indexes.edgeIndexesByPerson.length !== runtime.people.length) {
    throw new GraphLoadError('The graph adjacency index is incomplete.');
  }
  if (manifest.counts.inclusionRoles !== undefined) {
    const roleCounts = {
      baseline: runtime.people.filter((person) => person.inclusionRole === 'baseline').length,
      connector: runtime.people.filter((person) => person.inclusionRole === 'connector').length,
      context: runtime.people.filter((person) => person.inclusionRole === 'context').length,
    };
    if (
      roleCounts.baseline !== manifest.counts.inclusionRoles.baseline ||
      roleCounts.connector !== manifest.counts.inclusionRoles.connector ||
      roleCounts.context !== manifest.counts.inclusionRoles.context
    ) {
      throw new GraphLoadError('The graph inclusion-role counts do not match the manifest.');
    }
  }
  if (
    runtime.indexes.hubPersonIndexes.some(
      (personIndex) => personIndex < 0 || personIndex >= runtime.people.length,
    )
  ) {
    throw new GraphLoadError('The graph hub index contains an invalid person reference.');
  }
}

function assertDiscoveryCompatible(
  manifest: RuntimeGraphManifest,
  discovery: RuntimeDiscoveryDataset,
): void {
  if (
    discovery.datasetVersion !== manifest.datasetVersion ||
    discovery.configVersion !== manifest.configVersion ||
    discovery.policyVersion !== manifest.policyVersion ||
    discovery.layoutVersion !== manifest.layoutVersion
  ) {
    throw new GraphLoadError(
      'The graph manifest and discovery asset are from incompatible versions.',
    );
  }
  if (
    discovery.people.length !== manifest.counts.people ||
    discovery.featuredTraces.length !== manifest.counts.featuredTraces ||
    discovery.relationshipCounts.personal + discovery.relationshipCounts.family !==
      manifest.counts.relationships
  ) {
    throw new GraphLoadError('The discovery counts do not match the graph manifest.');
  }
  const peopleById = new Map(discovery.people.map((person) => [person.id, person]));
  if (peopleById.size !== discovery.people.length) {
    throw new GraphLoadError('The discovery asset contains duplicate people.');
  }
  if (
    new Set(discovery.starterPersonIds).size !== discovery.starterPersonIds.length ||
    discovery.starterPersonIds.some(
      (personId) =>
        !peopleById.has(personId) || peopleById.get(personId)?.inclusionRole === 'context',
    )
  ) {
    throw new GraphLoadError('The discovery asset contains an invalid starter person.');
  }
  if (
    discovery.featuredTraces.some(
      (trace) => !peopleById.has(trace.source) || !peopleById.has(trace.target),
    )
  ) {
    throw new GraphLoadError('The discovery asset contains an unavailable featured endpoint.');
  }
}

async function compatibleManifest(options: LoadOptions): Promise<RuntimeGraphManifest> {
  if (options.manifest !== undefined) {
    return options.manifest;
  }
  return loadProductionManifest(options);
}

export async function loadProductionGraph(options: LoadOptions = {}): Promise<GraphDataset> {
  const fetcher = options.fetcher ?? browserFetch;
  const manifest = await compatibleManifest(options);
  let runtime: RuntimeGraphDataset;
  if (manifest.schemaVersion === 3) {
    const graphText = await fetchText({
      fetcher,
      url: assetUrl(manifest.graph.path, 'graph'),
      cache: 'force-cache',
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
    reportPhase(options, 'graph-bytes');
    if ((await sha256(graphText)) !== manifest.graph.sha256) {
      throw new GraphLoadError('The graph asset failed its integrity check.');
    }
    reportPhase(options, 'graph-verified');
    try {
      runtime = RuntimeGraphDatasetSchema.parse(JSON.parse(graphText) as unknown);
    } catch {
      throw new GraphLoadError('The graph data asset is invalid.');
    }
  } else {
    const contextDescriptor = manifest.context;
    if (contextDescriptor === undefined) {
      throw new GraphLoadError('The graph manifest is missing its context partition.');
    }
    const evidenceDescriptor = manifest.schemaVersion === 5 ? manifest.evidence : undefined;
    if (manifest.schemaVersion === 5 && evidenceDescriptor === undefined) {
      throw new GraphLoadError('The graph manifest is missing its evidence partition.');
    }
    const graphPromise = fetchText({
      fetcher,
      url: assetUrl(manifest.graph.path, 'graph'),
      cache: 'force-cache',
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    }).then((text) => {
      reportPhase(options, 'graph-bytes');
      return text;
    });
    const contextPromise = fetchText({
      fetcher,
      url: assetUrl(contextDescriptor.path, 'context'),
      cache: 'force-cache',
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    }).then((text) => {
      reportPhase(options, 'context-bytes');
      return text;
    });
    const evidencePromise =
      evidenceDescriptor === undefined
        ? Promise.resolve<string | undefined>(undefined)
        : fetchText({
            fetcher,
            url: assetUrl(evidenceDescriptor.path, 'evidence'),
            cache: 'force-cache',
            ...(options.signal === undefined ? {} : { signal: options.signal }),
          }).then((text) => {
            reportPhase(options, 'evidence-bytes');
            return text;
          });
    const [graphText, contextText, evidenceText] = await Promise.all([
      graphPromise,
      contextPromise,
      evidencePromise,
    ]);
    const [graphDigest, contextDigest, evidenceDigest] = await Promise.all([
      sha256(graphText),
      sha256(contextText),
      evidenceText === undefined ? Promise.resolve(undefined) : sha256(evidenceText),
    ]);
    if (graphDigest !== manifest.graph.sha256) {
      throw new GraphLoadError('The graph asset failed its integrity check.');
    }
    reportPhase(options, 'graph-verified');
    if (contextDigest !== contextDescriptor.sha256) {
      throw new GraphLoadError('The context asset failed its integrity check.');
    }
    reportPhase(options, 'context-verified');
    if (
      evidenceDescriptor !== undefined &&
      (evidenceText === undefined || evidenceDigest !== evidenceDescriptor.sha256)
    ) {
      throw new GraphLoadError('The evidence asset failed its integrity check.');
    }
    if (evidenceDescriptor !== undefined) {
      reportPhase(options, 'evidence-verified');
    }
    try {
      const core = RuntimeGraphCoreDatasetSchema.parse(JSON.parse(graphText) as unknown);
      const context = RuntimeGraphContextDatasetSchema.parse(JSON.parse(contextText) as unknown);
      assertPartitionMetadata(manifest, core);
      assertPartitionMetadata(manifest, context);
      if (evidenceText === undefined) {
        runtime = mergeRuntimeGraphPartitions(core, context);
      } else {
        const evidence = RuntimeGraphEvidenceDatasetSchema.parse(
          JSON.parse(evidenceText) as unknown,
        );
        assertPartitionMetadata(manifest, evidence);
        runtime = mergeRuntimeGraphPartitions(core, context, evidence);
      }
    } catch (error: unknown) {
      if (error instanceof GraphLoadError) {
        throw error;
      }
      throw new GraphLoadError('The graph partition data is invalid.');
    }
  }
  assertCompatible(manifest, runtime);
  reportPhase(options, 'graph-ready');
  return toGraphDataset(runtime);
}

export async function loadProductionDiscovery(
  options: LoadOptions = {},
): Promise<RuntimeDiscoveryDataset> {
  const fetcher = options.fetcher ?? browserFetch;
  const manifest = await compatibleManifest(options);
  const discoveryText = await fetchText({
    fetcher,
    url: assetUrl(manifest.discovery.path, 'discovery'),
    cache: 'force-cache',
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  reportPhase(options, 'discovery-bytes');
  if ((await sha256(discoveryText)) !== manifest.discovery.sha256) {
    throw new GraphLoadError('The discovery asset failed its integrity check.');
  }
  reportPhase(options, 'discovery-verified');
  let discovery: RuntimeDiscoveryDataset;
  try {
    discovery = RuntimeDiscoveryDatasetSchema.parse(JSON.parse(discoveryText) as unknown);
  } catch {
    throw new GraphLoadError('The discovery data asset is invalid.');
  }
  assertDiscoveryCompatible(manifest, discovery);
  reportPhase(options, 'discovery-ready');
  return discovery;
}

export async function loadProductionManifest(
  options: Omit<LoadOptions, 'manifest'> = {},
): Promise<RuntimeGraphManifest> {
  const fetcher = options.fetcher ?? browserFetch;
  const manifestText = await fetchText({
    fetcher,
    url: MANIFEST_URL,
    cache: 'no-cache',
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  let manifest: RuntimeGraphManifest;
  try {
    manifest = RuntimeGraphManifestSchema.parse(JSON.parse(manifestText) as unknown);
  } catch {
    throw new GraphLoadError('The graph manifest is invalid.');
  }
  reportPhase(options, 'manifest-ready');
  return manifest;
}
